import { PaymentMode } from '../types/paymentMethodSet';
import type { VendorAccount } from '../types/paymentMethodSet';
import type { SfPaymentMethodComponent, SfPaymentMethodComponentConstructor } from './sfp-payment-method-component';
import type {
  SalesforcePaymentsConfig,
  SavedPaymentMethod,
  SalesforcePaymentsCheckoutConfig,
  SalesforcePaymentsExpressConfig,
  SalesforcePaymentsSetupConfig,
} from '../types/config';
import type { SfpVendor } from './sfp-vendor';
import type { PaymentIntentRequest } from '../types/paymentRequest';
import type { SfpSavedPaymentMethodComponent } from './sfp-saved-payment-method-component';
import type { Component } from '../components/component';
import type { PaymentRequest } from '../types/paymentRequest';
import type { SfpVendorWithTemplates } from '../components/template/sfp-vendor-with-templates';
import type { AbstractTemplate } from '../components/template/abstract-template';
import type { Checkout } from '../components/componentTypes/checkout';
import { ComponentType, PaymentMethodElement, Vendor } from '../types/paymentMethodSet';
import { PaymentMethodType } from '../types/paymentMethodSet';
import { loadCSS, loadScriptAsync } from '../util/load';
import { createSfpAdyenWeb } from './adyen/sfp-adyen-web';
import type { MetadataReader } from '../util/metadata-reader';
import type { VendorPaymentMethodElement } from '../types/component';
import { CheckoutTemplate } from '../components/template/checkout-template';
import { createSfpAdyenSavedPaymentMethodComponent } from './adyen/sfp-adyen-saved-payment-method-component';
import { ExpressTemplate } from '../components/template/express-template';
import { createSfpAdyenExpressApplePay } from './adyen/sfp-adyen-express-apple-pay';
import { createSfpAdyenExpressGooglePay } from './adyen/sfp-adyen-express-google-pay';
import { SetupTemplate } from '../components/template/setup-template';
import type { Setup } from '../components/componentTypes/setup';
import type { Express } from '../components/componentTypes/express';
import type SfpContext from '../sfp-context';
import { ResponseCode } from '../types/paymentResponse';
import type { PaymentResponse } from '../types/paymentResponse';

declare global {
  interface Window {
    AdyenCheckout: any;
    AdyenStyles: any;
  }
}

const EXPRESS_MAPPINGS = [
  {
    vendor: Vendor.ADYEN,
    paymentMethodType: PaymentMethodType.APPLEPAY,
    paymentMethodElement: PaymentMethodElement.ADYEN_EXPRESS_APPLEPAY,
    distinct: true,
  },
  {
    vendor: Vendor.ADYEN,
    paymentMethodType: PaymentMethodType.GOOGLEPAY,
    paymentMethodElement: PaymentMethodElement.ADYEN_EXPRESS_GOOGLEPAY,
    distinct: true,
  },
  {
    vendor: Vendor.PAYPAL,
    paymentMethodType: PaymentMethodType.PAYPAL,
    paymentMethodElement: PaymentMethodElement.PAYPAL_PAYPAL,
  },
  {
    vendor: Vendor.PAYPAL,
    paymentMethodType: PaymentMethodType.VENMO,
    paymentMethodElement: PaymentMethodElement.PAYPAL_VENMO,
  },
];

export class SfpAdyen implements SfpVendor, SfpVendorWithTemplates {
  paymentMethodDelegator = new Map<PaymentMethodElement, SfPaymentMethodComponentConstructor>([
    [PaymentMethodElement.ADYEN_WEB, createSfpAdyenWeb],
    [PaymentMethodElement.ADYEN_SAVABLE_PAYMENT_METHOD, createSfpAdyenSavedPaymentMethodComponent],
    [PaymentMethodElement.ADYEN_EXPRESS_APPLEPAY, createSfpAdyenExpressApplePay],
    [PaymentMethodElement.ADYEN_EXPRESS_GOOGLEPAY, createSfpAdyenExpressGooglePay],
  ]);

  SCRIPT_URL = 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/5.71.3/adyen.js';
  SCRIPT_INTEGRITY = 'sha384-h7QxpVlO2smphapKzNlkdxIgFRkuoKQJLZhwfgCpU6Jjy++gKbc+r6mwn90P69Sh';
  CSS_URL = 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/5.71.3/adyen.css';
  CSS_INTEGRITY = 'sha384-5MvB4RnzvviA3VBT4KYABZ4HXNZG5LRqREEgd41xt/pf/QvKmsj2O9GuNuywRXx9';

  adyen: any;

  async load(
    _account: VendorAccount,
    context: SfpContext,
    _config?: SalesforcePaymentsConfig,
    _paymentRequest?: PaymentRequest
  ): Promise<void> {
    if (!window.AdyenCheckout) {
      const reportLoadError: () => void = () =>
        context.report({
          code: 'ADYEN_SCRIPT_LOAD_ERROR',
          vendor: Vendor.ADYEN,
          type: 'SCRIPT_LOAD_ERROR',
          message: 'Failed to load Adyen script',
        });
      await loadScriptAsync(this.SCRIPT_URL, true, 'text/javascript', this.SCRIPT_INTEGRITY)
        .then((loaded) => {
          if (!loaded.status) {
            reportLoadError();
            throw new Error('Failed to load Adyen with error: ' + loaded.message);
          }
        })
        .catch((err) => {
          reportLoadError();
          throw new Error('Failed to load Adyen with error: ' + err);
        });
    }
    this.adyen = window.AdyenCheckout;

    if (!window.AdyenStyles) {
      await loadCSS(this.CSS_URL, this.CSS_INTEGRITY)
        .then((loaded) => {
          if (loaded.status) {
            window.AdyenStyles = true;
          } else {
            throw new Error('Failed to load Adyen with error: ' + loaded.message);
          }
        })
        .catch((err) => {
          throw new Error('Failed to load Adyen with error: ' + err);
        });
    }
  }

  getTemplate(
    config: SalesforcePaymentsConfig,
    metadataReader: MetadataReader,
    component: Component
  ): AbstractTemplate {
    switch (component.TYPE) {
      case ComponentType.CHECKOUT:
        return new CheckoutTemplate(
          config as SalesforcePaymentsCheckoutConfig,
          component as Checkout,
          (vendor: Vendor, paymentMethodType: PaymentMethodType) => {
            if (vendor === Vendor.ADYEN) {
              if (metadataReader.canSavePayment(paymentMethodType)) {
                return {
                  vendor: Vendor.ADYEN,
                  paymentMethodType,
                  paymentMethodElement: PaymentMethodElement.ADYEN_SAVABLE_PAYMENT_METHOD,
                  distinct: true,
                };
              } else {
                return {
                  vendor: Vendor.ADYEN,
                  paymentMethodType,
                  paymentMethodElement: PaymentMethodElement.ADYEN_WEB,
                  distinct: true,
                };
              }
            } else if (vendor === Vendor.PAYPAL) {
              if (paymentMethodType === PaymentMethodType.PAYPAL) {
                return {
                  vendor: Vendor.PAYPAL,
                  paymentMethodType: PaymentMethodType.PAYPAL,
                  paymentMethodElement: PaymentMethodElement.PAYPAL_PAYPAL,
                };
              } else if (paymentMethodType === PaymentMethodType.VENMO) {
                return {
                  vendor: Vendor.PAYPAL,
                  paymentMethodType: PaymentMethodType.VENMO,
                  paymentMethodElement: PaymentMethodElement.PAYPAL_VENMO,
                };
              }
            }
          }
        );
      case ComponentType.EXPRESS:
        return new ExpressTemplate(
          config as SalesforcePaymentsExpressConfig,
          component as Express,
          (vendor: Vendor, paymentMethodType: PaymentMethodType) =>
            EXPRESS_MAPPINGS.find(
              (entry: any) => entry.vendor === vendor && entry.paymentMethodType === paymentMethodType
            )
        );
      case ComponentType.SETUP:
        return new SetupTemplate(
          config as SalesforcePaymentsSetupConfig,
          component as Setup,
          (vendor: Vendor, paymentMethodType: PaymentMethodType) => {
            if (metadataReader.canSavePayment(paymentMethodType)) {
              return {
                vendor: Vendor.ADYEN,
                paymentMethodType,
                paymentMethodElement: PaymentMethodElement.ADYEN_SAVABLE_PAYMENT_METHOD,
                distinct: true,
              };
            }
          }
        );
      default:
        throw new Error(`Adyen template not supported for ${component.TYPE}`);
    }
  }

  async getPaymentMethodComponent(
    parentComponent: Component,
    paymentMethod: VendorPaymentMethodElement,
    metadataReader: MetadataReader,
    config: SalesforcePaymentsConfig,
    paymentRequest: PaymentIntentRequest,
    paymentMode: PaymentMode
  ): Promise<SfPaymentMethodComponent> {
    const component: SfPaymentMethodComponent = this.paymentMethodDelegator.get(paymentMethod.paymentMethodElement)(
      parentComponent,
      this.adyen,
      null,
      metadataReader,
      config,
      paymentRequest,
      paymentMethod,
      paymentMode
    );
    await component.initialize();
    return component;
  }

  getSavedPaymentMethodComponent(
    parentComponent: Checkout,
    paymentMethod: VendorPaymentMethodElement,
    savedPaymentMethod: SavedPaymentMethod,
    metadataReader: MetadataReader,
    config?: SalesforcePaymentsConfig,
    paymentRequest?: PaymentIntentRequest
  ): Promise<SfpSavedPaymentMethodComponent> {
    const component: SfPaymentMethodComponent = this.paymentMethodDelegator.get(paymentMethod.paymentMethodElement)(
      parentComponent,
      this.adyen,
      null,
      metadataReader,
      config,
      paymentRequest,
      paymentMethod,
      PaymentMode.MULTISTEP
    );
    const spmComponent = component.asSavedPaymentMethodComponent();
    if (spmComponent) {
      spmComponent.initializeSavedPaymentMethod(savedPaymentMethod);
      return Promise.resolve(spmComponent);
    }
    return null;
  }

  async getPaymentStatusForRedirect(urlParams: URLSearchParams): Promise<PaymentResponse> {
    if (urlParams.get('redirectResult')) {
      return Promise.resolve({
        responseCode: ResponseCode.SUCCESS,
        data: {
          paymentData: {
            id: urlParams.get('uuid'),
            uuid: urlParams.get('uuid'),
            paymentGatewayId: urlParams.get('gatewayId'),
            redirectResultId: urlParams.get('redirectResult'),
            type: urlParams.get('paymentMethodType'),
            isManualCapture: urlParams.get('isManualCapture'),
          },
          paymentToken: urlParams.get('uuid'),
          billingDetails: {
            name: urlParams.get('billingName'),
            email: urlParams.get('billingEmail'),
            phone: urlParams.get('billingPhone'),
            address: {
              line1: urlParams.get('billingLine1'),
              line2: urlParams.get('billingLine2'),
              city: urlParams.get('billingCity'),
              state: urlParams.get('billingState'),
              postalCode: urlParams.get('billingPostalCode'),
              country: urlParams.get('billingCountry'),
            },
          },
          status: 'succeeded',
        },
      });
    } else {
      return Promise.resolve({
        responseCode: ResponseCode.FAILURE,
        data: {
          error: 'Payment Intent confirmation did not succeed',
          status: 'failed',
        },
      });
    }
  }
}

export async function loadAdyenModule(
  account: VendorAccount,
  context: SfpContext,
  config?: SalesforcePaymentsConfig,
  paymentRequest?: PaymentRequest
): Promise<SfpAdyen> {
  const sfpAdyen = new SfpAdyen();
  await sfpAdyen.load(account, context, config, paymentRequest);
  return sfpAdyen;
}
